import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  IconButton,
  Box,
  Paper,
  Button,
  LinearProgress,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  AccessTime as AccessTimeIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import TaskDialog from './components/TaskDialog';
import { useAuth } from '../contexts/AuthContext';
import { getDueDateInfo } from '../utils/dateUtils';
import DraggableTask from './components/DraggableTask';
import { getProgress } from '../utils/taskUtils';
import config from '../config';

// Custom hook to detect mobile devices
const useIsMobile = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('sm'));
};

function PlannerApp() {
  const { token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [mobileTab, setMobileTab] = useState(0);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchTasks();
  }, [token]);

  // Effect to reset mobile tab when showArchived changes
  useEffect(() => {
    if (mobileTab === 3 && !showArchived) {
      setMobileTab(0);
    }
  }, [showArchived, mobileTab]);

  const fetchTasks = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      const sortedData = data.sort((a, b) => a.order - b.order);
      setTasks(sortedData);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const handleCreateTask = async (newTask) => {
    try {
      const response = await fetch(`${config.apiUrl}/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newTask)
      });
      const createdTask = await response.json();
      setTasks([...tasks, createdTask]);
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      console.log('Updating task with data:', updates);
      const existingTask = tasks.find(t => t._id === taskId);
      const taskUpdate = {
        ...updates,
        backgroundColor: updates.backgroundColor || existingTask.backgroundColor || '#ffffff',
        status: updates.status || existingTask.status,
        order: typeof updates.order === 'number' ? updates.order : existingTask.order
      };

      const response = await fetch(`${config.apiUrl}/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(taskUpdate)
      });
      const updatedTask = await response.json();
      console.log('Received updated task with color:', {
        taskId,
        backgroundColor: updatedTask.backgroundColor,
        fullTask: updatedTask
      });
      setTasks(tasks.map(task => 
        task._id === taskId ? updatedTask : task
      ));
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) {
      return;
    }

    const { source, destination } = result;
    console.log('Drag result:', {
      draggableId: result.draggableId,
      source: source,
      destination: destination
    });
    
    // If dropped in a different list
    if (source.droppableId !== destination.droppableId) {
      const task = tasks.find(t => t._id === result.draggableId);
      const update = {
        title: task.title,
        description: task.description,
        backgroundColor: task.backgroundColor,
        status: destination.droppableId,
        archived: destination.droppableId === 'archived',
        checklist: (task.checklist || []).map(item => {
          const mappedItem = {
            _id: item._id,
            text: item.text,
            description: item.description || '',
            completed: item.completed || false,
            createdBy: typeof item.createdBy === 'string' ? item.createdBy : item.createdBy?._id
          };
          
          if (item.createdAt) mappedItem.createdAt = item.createdAt;
          if (item.lastModifiedBy?._id) mappedItem.lastModifiedBy = item.lastModifiedBy._id;
          if (item.lastModifiedAt) mappedItem.lastModifiedAt = item.lastModifiedAt;
          if (item.completedBy?._id) mappedItem.completedBy = item.completedBy._id;
          if (item.completedAt) mappedItem.completedAt = item.completedAt;
          
          return mappedItem;
        })
      };
      await handleUpdateTask(task._id, update);
      return;
    }

    // If reordering within the same list
    const items = Array.from(tasks);
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);
    
    // Update order values
    const updatedTasks = items.map((task, index) => ({
      ...task,
      order: index
    }));
    
    setTasks(updatedTasks);

    try {
      const payload = {
        tasks: updatedTasks.map((task, index) => ({
          id: task._id,
          order: index
        }))
      };
      console.log('Sending reorder payload:', JSON.stringify(payload, null, 2));

      await fetch(`${config.apiUrl}/tasks/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      console.error('Error saving task order:', error);
    }
  };

  const getTasksByStatus = (status) => {
    return tasks.filter(task => task.status === status).sort((a, b) => a.order - b.order);
  };

  const renderColumn = (title, status) => {
    const columnTasks = getTasksByStatus(status);
    
    return (
      <Droppable droppableId={status}>
        {(provided) => (
          <Paper
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              p: { xs: 1, sm: 2 },
              bgcolor: 'grey.50',
              height: '100%',
              minHeight: isMobile ? '50vh' : '70vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              {title} ({columnTasks.length})
            </Typography>
            <Box 
              sx={{ 
                flex: 1,
                overflowY: 'auto',
                marginRight: -1,
                paddingRight: 1
              }}
            >
              {columnTasks.map((task, index) => (
                <DraggableTask
                  key={task._id}
                  task={task}
                  index={index}
                  onEdit={() => {
                    setSelectedTask(task);
                    setDialogOpen(true);
                  }}
                  onArchive={handleArchiveTask}
                  isMobile={isMobile}
                />
              ))}
              {provided.placeholder}
            </Box>
          </Paper>
        )}
      </Droppable>
    );
  };

  const handleArchiveTask = async (task) => {
    try {
      const update = {
        title: task.title,
        description: task.description,
        backgroundColor: task.backgroundColor,
        status: !task.archived ? 'archived' : 'todo',
        archived: !task.archived,
        checklist: (task.checklist || []).map(item => {
          const mappedItem = {
            _id: item._id,
            text: item.text,
            description: item.description || '',
            completed: item.completed || false,
            createdBy: typeof item.createdBy === 'string' ? item.createdBy : item.createdBy?._id
          };
          
          if (item.createdAt) mappedItem.createdAt = item.createdAt;
          if (item.lastModifiedBy?._id) mappedItem.lastModifiedBy = item.lastModifiedBy._id;
          if (item.lastModifiedAt) mappedItem.lastModifiedAt = item.lastModifiedAt;
          if (item.completedBy?._id) mappedItem.completedBy = item.completedBy._id;
          if (item.completedAt) mappedItem.completedAt = item.completedAt;
          
          return mappedItem;
        })
      };
      console.log('Sending update:', update);
      await handleUpdateTask(task._id, update);
    } catch (error) {
      console.error('Error archiving task:', error);
      console.error('Task data:', task);
    }
  };

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: 4, 
        mb: 4,
        height: 'calc(100vh - 64px)', // Adjust based on your header height
        display: 'flex',
        flexDirection: 'column',
        px: { xs: 1, sm: 2, md: 3 }
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Typography variant="h4">Tasks</Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Button
            variant="outlined"
            startIcon={<ArchiveIcon />}
            onClick={() => setShowArchived(prev => !prev)}
            fullWidth={isMobile}
            size={isMobile ? "small" : "medium"}
          >
            {showArchived ? 'Hide Archived' : 'Show Archived'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedTask(null);
              setDialogOpen(true);
            }}
            fullWidth={isMobile}
            size={isMobile ? "small" : "medium"}
          >
            New Task
          </Button>
        </Box>
      </Box>

      <DragDropContext onDragEnd={handleDragEnd}>
        {isMobile ? (
          // Mobile layout with tabs for vertical columns
          <>
            <Tabs 
              value={mobileTab} 
              onChange={(e, newValue) => setMobileTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label={`Planned (${getTasksByStatus('todo').length})`} />
              <Tab label={`In Progress (${getTasksByStatus('inProgress').length})`} />
              <Tab label={`Completed (${getTasksByStatus('completed').length})`} />
              {showArchived && <Tab label={`Archived (${getTasksByStatus('archived').length})`} />}
            </Tabs>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {mobileTab === 0 && renderColumn('Planned', 'todo')}
              {mobileTab === 1 && renderColumn('In Progress', 'inProgress')}
              {mobileTab === 2 && renderColumn('Completed', 'completed')}
              {mobileTab === 3 && showArchived && renderColumn('Archived', 'archived')}
            </Box>
          </>
        ) : (
          // Desktop layout with horizontal grid
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: showArchived ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)', 
            gap: 3,
            flex: 1,
            minHeight: 0  // Important for grid layout
          }}>
            {renderColumn('Planned', 'todo')}
            {renderColumn('In Progress', 'inProgress')}
            {renderColumn('Completed', 'completed')}
            {showArchived && renderColumn('Archived', 'archived')}
          </Box>
        )}
      </DragDropContext>

      <TaskDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedTask(null);
        }}
        task={selectedTask}
        onSave={async (data) => {
          if (selectedTask) {
            await handleUpdateTask(selectedTask._id, data);
          } else {
            await handleCreateTask(data);
          }
          setDialogOpen(false);
          setSelectedTask(null);
        }}
      />
    </Container>
  );
}

export default PlannerApp; 